// @section 1 Initiatives route — editable register + weekly proposal queue
// Reads GET /api/hq/initiatives (exec_team.initiatives joined to
// analytics.v_initiative_session_cost) and GET /api/hq/initiative-proposals
// (serving.initiative_proposals, [] when the curator table doesn't exist yet).
// Edits POST back through /api/hq/initiatives so the status-history trigger
// fires. Each row shows its attributed sessions / hours / cost / goal-rate so
// the register is read in the context of what work it actually consumed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../components/Toast';
import {
  getInitiatives,
  getInitiativeProposals,
  upsertInitiative,
  type InitiativeRow,
  type InitiativeProposal,
  type UpsertInitiativeBody,
} from '../api';

// @subsection 1.1 Display metadata

const STATUSES = ['open', 'committed', 'paused', 'shipped', 'killed'] as const;
type Status = (typeof STATUSES)[number];

const STATUS_CLASS: Record<string, string> = {
  open: 'border-paper-100/25 text-paper-100/80',
  committed: 'border-accent/50 text-accent',
  paused: 'border-watch/50 text-watch',
  shipped: 'border-ok/50 text-ok',
  killed: 'border-stall/50 text-stall',
};

// @subsection 1.2 Helpers

function usd(v: number | null | undefined): string {
  return v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
}
function pct(v: number | null | undefined): string {
  return v == null ? '—' : `${Math.round(v)}%`;
}
function hrs(v: number | null | undefined): string {
  return v == null ? '—' : `${Math.round(v)}h`;
}

// Best-effort read of a proposal field across snake_case / camelCase shapes
// (the curator table is schema-agnostic, so we probe both).
function field(p: InitiativeProposal, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = p[k];
    if (v != null && v !== '') return String(v);
  }
  return undefined;
}

// @section 2 Page component

export default function Initiatives({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<InitiativeRow[] | null>(null);
  const [proposals, setProposals] = useState<InitiativeProposal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null); // slug being edited
  const [saving, setSaving] = useState(false);

  // @block 2.1 Loaders
  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        getInitiatives(),
        getInitiativeProposals().catch(() => [] as InitiativeProposal[]),
      ]);
      setRows(r);
      setProposals(p);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // @block 2.2 Save (upsert) — persists, then refetches so the cost rollup and
  // any trigger-side effects (status history) are reflected.
  const save = useCallback(async (body: UpsertInitiativeBody) => {
    setSaving(true);
    try {
      await upsertInitiative(body);
      toast(`✓ Saved ${body.slug}`);
      setEditing(null);
      await load();
    } catch (e) {
      toast(`Save failed: ${e}`);
    } finally {
      setSaving(false);
    }
  }, [load]);

  // @block 2.3 Accept a proposal — map its fields onto an upsert. Unknown shapes
  // still apply whatever slug/title/status/priority/next-action they carry.
  const acceptProposal = useCallback((p: InitiativeProposal) => {
    const slug = field(p, 'slug', 'initiative_slug', 'initiativeSlug');
    if (!slug) { toast('Proposal has no slug to apply.'); return; }
    const priorityRaw = field(p, 'priority_rank', 'priorityRank');
    void save({
      slug,
      title: field(p, 'title'),
      status: field(p, 'status'),
      priorityRank: priorityRaw != null ? Number(priorityRaw) : undefined,
      nextAction: field(p, 'next_action', 'nextAction'),
      descriptionMd: field(p, 'description_md', 'descriptionMd'),
    });
  }, [save]);

  const windowCost = useMemo(
    () => (rows ?? []).reduce((a, r) => a + (r.recordedCostUsd ?? 0), 0),
    [rows],
  );

  return (
    <div className={embedded ? '' : 'md:-mx-10 md:-my-8'}>
      <section className={embedded ? 'text-paper-100 font-sans' : 'bg-ink-950 text-paper-100 min-h-screen font-sans'}>
        <div className={embedded ? 'max-w-[1400px] mx-auto' : 'px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto'}>
          {/* @block 2.4 Header (hidden when embedded in the Command module) */}
          {!embedded && (
            <>
              <div className="flex items-center justify-between text-xs mb-6">
                <div className="text-muted">
                  <span className="text-paper-100">Atlas</span>
                  <span className="opacity-40"> / </span>
                  <span className="text-paper-100/80">Initiatives</span>
                </div>
                {rows && (
                  <div className="text-muted">
                    {rows.length} initiatives · {usd(windowCost)} attributed
                  </div>
                )}
              </div>

              <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Register + weekly proposals</div>
                <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100">Initiatives</h1>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">{error}</div>
          )}
          {!rows && !error && <em className="text-muted">Loading…</em>}

          {/* @block 2.5 Register */}
          {rows && (
            <div className="space-y-3 mb-12">
              {rows.map((r) =>
                editing === r.slug ? (
                  <InitiativeEditor key={r.slug} row={r} saving={saving}
                    onCancel={() => setEditing(null)} onSave={save} />
                ) : (
                  <InitiativeCard key={r.slug} row={r} onEdit={() => setEditing(r.slug)} />
                ),
              )}
            </div>
          )}

          {/* @block 2.6 Proposal queue */}
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[2px] text-muted mb-3">
              Weekly proposal queue {proposals.length > 0 && `· ${proposals.length}`}
            </div>
            {proposals.length === 0 ? (
              <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5 text-sm text-muted">
                No pending proposals. The weekly curator writes diffs to
                <span className="font-mono text-paper-100/70"> serving.initiative_proposals</span>;
                this queue stays empty until that job runs (or its table exists).
              </div>
            ) : (
              <div className="space-y-3">
                {proposals.map((p, i) => (
                  <ProposalCard key={i} proposal={p} onAccept={() => acceptProposal(p)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// @section 3 Sub-components

// @block 3.1 Read-only register row
function InitiativeCard({ row, onEdit }: { row: InitiativeRow; onEdit: () => void }) {
  return (
    <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-4 flex items-start gap-4">
      <div className="text-2xl font-bold text-paper-100/40 w-8 text-center tabular-nums">
        {row.priorityRank ?? '—'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-base font-semibold text-paper-100 truncate">{row.title}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_CLASS[row.status] ?? STATUS_CLASS.open}`}>
            {row.status}
          </span>
        </div>
        <div className="text-xs text-muted truncate">
          {row.nextAction ? <>Next: <span className="text-paper-100/70">{row.nextAction}</span></> : <em>No next action set.</em>}
        </div>
      </div>
      <div className="flex items-center gap-5 text-right shrink-0">
        <Stat label="Sessions" value={String(row.sessions)} />
        <Stat label="Hours" value={hrs(row.hours)} />
        <Stat label="Cost" value={usd(row.recordedCostUsd)} />
        <Stat label="Goal" value={pct(row.goalPct)} />
        <button onClick={onEdit}
          className="text-[11px] px-3 py-1.5 rounded-lg border border-paper-100/15 text-paper-100/80 hover:border-accent/50 hover:text-accent transition-colors">
          Edit
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-[52px]">
      <div className="text-sm font-semibold text-paper-100 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[1px] text-muted">{label}</div>
    </div>
  );
}

// @block 3.2 Inline editor — status / priority / next action / description
function InitiativeEditor({
  row, saving, onCancel, onSave,
}: {
  row: InitiativeRow;
  saving: boolean;
  onCancel: () => void;
  onSave: (b: UpsertInitiativeBody) => void;
}) {
  const [status, setStatus] = useState<Status>((row.status as Status) ?? 'open');
  const [priority, setPriority] = useState<string>(row.priorityRank?.toString() ?? '');
  const [nextAction, setNextAction] = useState(row.nextAction ?? '');
  const [description, setDescription] = useState(row.descriptionMd ?? '');

  const submit = () => {
    onSave({
      slug: row.slug,
      status,
      priorityRank: priority.trim() === '' ? null : Number(priority),
      nextAction,
      descriptionMd: description,
    });
  };

  return (
    <div className="rounded-xl border border-accent/40 bg-ink-900 p-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-base font-semibold text-paper-100">{row.title}</span>
        <span className="text-[11px] text-muted font-mono">{row.slug}</span>
      </div>

      <div className="grid grid-cols-[120px_120px_1fr] gap-3 mb-3">
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-[1px] text-muted mb-1">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as Status)}
            className="w-full bg-ink-950 border border-paper-100/15 rounded-lg px-2 py-1.5 text-paper-100">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-[1px] text-muted mb-1">Priority</span>
          <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)}
            className="w-full bg-ink-950 border border-paper-100/15 rounded-lg px-2 py-1.5 text-paper-100 tabular-nums" />
        </label>
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-[1px] text-muted mb-1">Next action</span>
          <input type="text" value={nextAction} onChange={(e) => setNextAction(e.target.value)}
            placeholder="The single next concrete step"
            className="w-full bg-ink-950 border border-paper-100/15 rounded-lg px-2 py-1.5 text-paper-100" />
        </label>
      </div>

      <label className="text-xs block mb-3">
        <span className="block text-[10px] uppercase tracking-[1px] text-muted mb-1">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          className="w-full bg-ink-950 border border-paper-100/15 rounded-lg px-2 py-1.5 text-paper-100 resize-y" />
      </label>

      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} disabled={saving}
          className="text-[11px] px-3 py-1.5 rounded-lg border border-paper-100/15 text-muted hover:text-paper-100 disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={saving}
          className="text-[11px] px-4 py-1.5 rounded-lg border border-accent/50 bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// @block 3.3 Proposal card — schema-agnostic; renders the diff and an Accept action
function ProposalCard({ proposal, onAccept }: { proposal: InitiativeProposal; onAccept: () => void }) {
  const title = field(proposal, 'title', 'slug', 'initiative_slug') ?? 'Proposal';
  const entries = Object.entries(proposal).filter(([, v]) => v != null && v !== '');
  return (
    <div className="rounded-xl border border-watch/30 bg-ink-900 p-4 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-paper-100 mb-2">{title}</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="text-xs flex gap-2 min-w-0">
              <span className="text-muted shrink-0">{k}:</span>
              <span className="text-paper-100/80 truncate">{String(v)}</span>
            </div>
          ))}
        </div>
      </div>
      <button onClick={onAccept}
        className="text-[11px] px-3 py-1.5 rounded-lg border border-ok/40 text-ok hover:bg-ok/10 shrink-0">
        Accept
      </button>
    </div>
  );
}
